import { get, isEmpty, isUndefined, isNil as isNill, isArray, size, lowerCase, trim } from "lodash";
import dayjs from "dayjs";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { saveAs } from "file-saver";
import CryptoJS from "crypto-js";

import { CloudEnvironmentConfig, DownloadUrl } from "../constants/AppConfigs";
import {
  centeneAmbetterPaPaRequestForm,
  claimsDispute,
  electronicsFundsTransferEnrollment,
  faxConfirmationForm,
  informationChangeForm,
  IS_LOCALHOST,
  managedHealthPlanList,
  oasisUpload,
  outOfNetworkServiceNotification,
  priorAuthorizationRequest,
  providerManual,
  REACT_APP_ENDPOINT_ENV,
  reAuthorizationRequestForm,
  referralFormNevadaOnly,
  resumptionOfCareRequestForm,
  startOfCareConfirmationForm,
} from "../constants/Config";
import Logger from "./logHandlers/Logger";
import {
  alphabetSpaceAndHyphen,
  onlyNumAlphabetRegex,
  regexMBI,
  removeSpecialChar,
} from "../constants/Regex";
import toastType from "./SnackbarProvider";
import { roleAccess1, UserRole } from "../constants/HomePage";
import { MyAccountModel, UserProvider } from "../models/MyAccount";
import { extractFields } from "../app/common/ListConversion";
import { ServiceProvider } from "../models/ServiceProvider";
import { Constants, Numbers, PayerIdInfo, SALT, SECRET_KEY, visitType, visitTypeHome } from "../constants/Referral";
import { DateFormats } from "../constants/Date";
import { SlotsPayload } from "../models/Referral";
import { CheckEligibility, InfoAddress } from "../models/Patient";
import { ApprovedAuthType, TextConstants } from "../constants/Patient";
import { FeatureAccess, MasterRole } from "../models/Master";
import { videoPlaceholderImage } from "../constants/ImagePaths";
import { DropdownOption, DropdownOptionKeys } from "../constants/FormComponents";
import { NppesStateList } from "../models/PhysicianDetails";
import { stateNameAbbreviations } from "../constants/StateAbbreviations";
import { FormattedNumber } from "./PhoneNumber";
import { DocumentType } from "../constants/DocumentUpload";
import { VERSION_CONFLICT_STATUS } from "../services/api";
import { BCBSMI, BCBSMIMINSOCDATE } from "../constants/FunctionalUtils";
import { PerformancePeriods } from "../models/Scorecard";
import { Language } from "../models/MemberDetails";
import { LANGUAGE_KEYWORDS, PatientLanguages } from "../constants/Master";

/**
 * Method to execute all init calls post config Load
 *
 */
export const initAfterConfigLoad = (): void => {
  Logger.suppressConsole();
};

/**
 * below function used for sending error response to api call
 * @param error
 * @returns
 */
export const ErrorResponse = (error: any) => {
  if (getValue(error, "status", "")) {
    switch (error.status) {
      case 422:
      case 400:
      case 404:
        return error;
      default:
        break;
    }
  }
};

//function to get the value at path of object
export const getValue = (
  inputObj: any,
  nestedProp: string,
  defaultValue?: any
) => {
  if (isUndefined(defaultValue)) {
    defaultValue = null;
  }
  return get(inputObj, nestedProp, defaultValue);
};

/**
 * allow only numbers or string as per regex filter
 * @param {any}  e - input field value typed in
 * @param {string} type - allowed filter type - onlyNumber or onlyText or onlyTextAndSpace
 *
 */
export const handleInputEvent = (e: any, type: string | undefined) => {
  const initialValue = e.target.value;
  const selectionStart = e.target.selectionStart;

  let newSelectionStart = selectionStart;

  switch (type) {
    case "onlyNumber":
      e.target.value = initialValue.replace(/\D*/g, "");
      break;
    case "onlyText":
      e.target.value = initialValue.replace(/[^a-zA-Z]/g, "");
      break;
    case "onlyTextAndSpace":
      e.target.value = initialValue.replace(/[^a-zA-Z\s]/g, "");
      break;
    case "onlyTextSpaceAndHyphen":
      e.target.value = initialValue.replace(alphabetSpaceAndHyphen, "");
      break;
    case "onlyNumAlphabetRegex":
      e.target.value = initialValue.replace(onlyNumAlphabetRegex, "");
      break;
  }
  if (initialValue !== e.target.value) {
    const removedCharsBeforeCursor = initialValue
      .substring(0, selectionStart)
      .split("")
      .filter((char: any) => !e.target.value.includes(char)).length;
    newSelectionStart = selectionStart - removedCharsBeforeCursor;
    e.target.setSelectionRange(newSelectionStart, newSelectionStart);
    e.stopPropagation();
  }
};

/**
 *
 * @param errors
 */
export const errorInToast = (errors: any) => {
  if (!isEmpty(errors)) {
    Object.keys(errors).forEach((field) => {
      errors[field].forEach((message: string) => {
        toastType.error(message);
      });
    });
  }
};

/**
 *
 * @param loggedInUser
 * @returns
 */
export const isAdmin = (loggedInUser: MyAccountModel) => {
  if (loggedInUser?.providerPortalUser?.roleType) {
    const { roleType } = loggedInUser.providerPortalUser;
    const allowedRoles = [UserRole.TANGO_ADMIN, UserRole.PROVIDER_ADMIN];
    if (allowedRoles.some((role) => roleType.includes(role))) {
      return true;
    }
  }
  return false;
};

/**
 *
 * @param list any list
 * @param includeOriginalObject boolean includeOriginalObject
 * @returns return list array with extracted fields
 */
export const getListResult = (list: any, masterServiceProvider?: ServiceProvider[]) => {
  const result = list.length > 0 ? list.map((v: any) => extractFields(v, masterServiceProvider)) : [];
  return result;
};

/**
 *
 * @param message
 * @param type
 */
export const ToastMessage = (message: string, type: number) => {
  switch (type) {
    case 1:
      toastType.success(message);
      break;
    case 2:
      toastType.error(message);
      break;
    case 3:
      toastType.warning(message);
      break;

    default:
      toastType.warning("Something went wrong!");
      break;
  }
};

/**
 * To get the health plan and benifit plan data from base64 encoded string and separated by "|||"
 * @param {string}  base64String - base64 healthplan id and label string concatenated with "|||"
 * @param {number} index - 0 for healthplan id and 1 for health plan name
 *
 * @returns {string} healthplan id or health plan name
 */
export const getHealthPlanValueType = (
  base64String: string | undefined,
  index: number
): string => {
  return !isUndefined(base64String) &&
    !isNill(base64String) &&
    isBase64String(base64String)
    ? atob(base64String).split("|||")[index]
    : "";
};

/**
 *
 * @param gender
 * @returns
 */
export const getGender = (gender?: string) => {
  if (gender === "M" || gender === "Male") {
    return "Male";
  } else if (gender === "F" || gender === "Female") {
    return "Female";
  } else if (gender === "U" || gender === "Unknown") {
    return "Unknown";
  } else {
    return "";
  }
};

/**
 *
 * @param zipCode
 * @returns
 */
export const getZipCode = (zipCode: string | null) => {
  if (!zipCode || isEmpty(zipCode)) {
    return "";
  }
  return zipCode.length >= 5 ? zipCode.slice(0, 5) : zipCode;
};
/**
 *
 * @param zipCode
 * @returns
 */

export const validateZipCode = (
  zipCode: NullableString
): boolean => {
  if (!zipCode) {
    return false;
  }
  const cleanedZipCode = zipCode && zipCode.replace(/\D/g, "");
  return cleanedZipCode.length === 5;
};

/**
 *
 * @param key
 * @param data
 * @returns
 */
export const setSessionStorage = (key: string, data: any) => {
  return sessionStorage.setItem(key, JSON.stringify(data));
};

/**
 *
 * @param key
 * @returns
 */
export const getSessionStorage = (key: string) => {
  const data = sessionStorage.getItem(key);
  if (data) {
    return JSON.parse(data);
  }
  return null;
};

/**Checking length for different type of array in the application */
export const length = (value: any) => {
  if (value !== undefined && value.length > 0) {
    return true;
  }
  return false;
};

/**
 *
 * @param strings any strings
 *
 * @returns concatinated string
 */
export const getConcatStringOrNull = (...strings: any[]) => {
  return strings
    .filter((str: string) => str != null && str !== undefined)
    .join(" ");
};

/*joining address with spaces and commas */
export const formatAddressString = (...strings: any[]) => {
  return strings
    .map((str) => (str != null && str !== undefined ? String(str).trim() : ""))
    .filter((str) => str !== "")
    .join(", ");
};

/**
 * @param {any}  date - a date string
 * @param {number} value - a value number
 * @param {any} type - type any
 *
 * @returns {any} following function used to add number of days/month in current provided date
 */
export const addDaysToDate = (date: string, value: number, type: any) => {
  return dayjs(date).add(value, type);
};

/** remove unwanted commas from strings*/
export const removeMultipleCommas = (text: string | null) => {
  return text && text.replace(/,+/g, ",");
};

/**
 * NPI Validation as per old Portal
 *
 */
const DIGIT_MATCH = /^\d+$/;
const RESIDUES = [0, 2, 4, 6, 8, 1, 3, 5, 7, 9];
export const luhn = (value: any) => {
  let result = false;

  if (value && DIGIT_MATCH.test(value)) {
    const sum = value
      .split("")
      .reverse()
      .map(function (c: any) {
        return parseInt(c);
      })
      .map(function (c: any, i: number) {
        return i % 2 === 0 ? c : RESIDUES[c];
      })
      .reduce(function (sum: any, c: any) {
        return sum + c;
      }, 0);
    result = sum % 10 === 0;
  }

  return result;
};

/**NPI validation as per old portal */
export const npi = (value: any) => {
  let result = false;
  if (value.length === 10) {
    result = luhn("80840".concat(value));
  }
  return result;
};

/**Following function used for filter Agency list for Prior Auth agency selection dropdown
 * future scop for filter on NPI
 */
export const filterOperatorServiceList = (
  masterServiceProviderState: ServiceProvider[],
  isPriorAuth: boolean,
  loggedInUser?: MyAccountModel
) => {
  //As per story 12488 Removed existing state validation logic
  const filterForProviderAdmin =
    loggedInUser && loggedInUser.userProviderList.length > Numbers.ZERO
      ? masterServiceProviderState.length > Numbers.ZERO &&
      masterServiceProviderState.filter(
          (item: any) =>
            loggedInUser.userProviderList &&
            loggedInUser.userProviderList.some(
              (itemSelectedProvider: UserProvider) =>
                itemSelectedProvider.serviceProviderId ===
                item.serviceProviderId && item.isPriorAuthEnable === true
            )
        )
      : [];
  if (isPriorAuth) {
    return loggedInUser &&
      loggedInUser.providerPortalUser.roleType === UserRole.TANGO_ADMIN
      ? masterServiceProviderState.filter(
        (itemSelectedProvider: ServiceProvider) => itemSelectedProvider.isPriorAuthEnable === true
      )
      : filterForProviderAdmin;
  }
  return masterServiceProviderState
};

/**following function used to return current date*/
export const currentDate = (isUtc?: boolean) => {
  if (isUtc) {
    return dayjs.utc().format();
  }
  return dayjs().format();
};

/**Checking isNil value */
export const isNil = (value: any) => {
  if (value === undefined || value === null) {
    return true;
  }
  return false;
};

/**to validate user should select at least 1 visit */
export const validateVisits = (values: any, setDisabled?: boolean) => {
  const requiredFieldNames = setDisabled ? visitType : visitTypeHome ;
  const filledFields = requiredFieldNames.filter(
    (fieldName: string) => values[fieldName] !== 0
  ).length;

  if (filledFields === 0) {
    return false;
  }
  return true;
};

export const isBase64 = (str: string) => {
  try {
    atob(str);
    return true;
  } catch (e) {
    return false;
  }
};

export const getDownloadUrl = (urlName: string) => {
  switch (urlName) {
    case DownloadUrl.PRIOR_AUTHORIZATION_REQUEST:
      return priorAuthorizationRequest;
    case DownloadUrl.REFERRAL_FORM_NEVADA_ONLY:
      return referralFormNevadaOnly;
    case DownloadUrl.CENTENE_AMBETTER_PA_PA_REQUEST_FORM:
      return centeneAmbetterPaPaRequestForm;
    case DownloadUrl.RESUMPTION_OF_CARE_REQUEST_FORM:
      return resumptionOfCareRequestForm;
    case DownloadUrl.RE_AUTHORIZATION_REQUEST_FORM:
      return reAuthorizationRequestForm;
    case DownloadUrl.START_OF_CARE_CONFIRMATION_FORM:
      return startOfCareConfirmationForm;
    case DownloadUrl.FAX_CONFIRMATION_FORM:
      return faxConfirmationForm;
    case DownloadUrl.OUT_OF_NETWORK_SERVICE_NOTIFICATION:
      return outOfNetworkServiceNotification;
    case DownloadUrl.ELECTRONICS_FUNDS_TRANSFER_ENROLLMENT:
      return electronicsFundsTransferEnrollment;
    case DownloadUrl.CLAIMS_DISPUTE:
      return claimsDispute;
    case DownloadUrl.INFORMATION_CHANGE_FORM:
      return informationChangeForm;
    case DownloadUrl.PROVIDER_MANUAL:
      return `${providerManual}?_t=${Date.now().toString()}`;
    case DownloadUrl.OASIS_UPLOAD:
      return oasisUpload;
    case DownloadUrl.MANAGED_HEALTH_PLAN_LIST:
      return managedHealthPlanList;
    default:
      return "";
  }
};

//Function to set scroll at top
export const scrollToTop = () => {
  return window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

/**Document name changes to avoid duplicate document error
 * param1: file name
 * param2: timestamp
 */
export const appendTimestampToFilename = (fileName: any, timestamp: any) => {
  const index = fileName.lastIndexOf(".");
  if (index === -1) {
    return fileName + "_" + timestamp;
  }
  const name = fileName.substring(0, index);
  const extension = fileName.substring(index);
  return name + "_" + timestamp + extension;
};

/**This function is used for get proper timestamp as per old portal
 * param1: file number
 */
export const getFormattedDateTime = (incrementalValue: number) => {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  const hr = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  const sec = String(date.getSeconds()).padStart(2, "0");

  return (
    "_" + month + day + year + "_" + hr + min + sec + "_" + incrementalValue
  );
};

/**
 * function is used for generate proper notes text
 * @param submittedBy
 * @param Referral_RequestorEmail
 * @param Referral_Fax
 * @param Referral_AgencyPhone
 * @param Referral_ApprovedAuthPreference
 * @returns
 */
export const generateAgencyDetailsNote = (
  submittedBy: string,
  Referral_RequestorEmail: string,
  Referral_Fax: string,
  Referral_AgencyPhone: string,
  Referral_ApprovedAuthPreference: string,
  Referral_SecondaryAddress: string,
  careType: string,
  nurseToOpen?: boolean
) => {
  return `Name: ${submittedBy}, Email: ${Referral_RequestorEmail}, Fax: ${Referral_Fax}, Phone: ${Referral_AgencyPhone}, Send Approved Auth To: ${Referral_ApprovedAuthPreference}, ${isEmpty(Referral_SecondaryAddress) ? "" : `Secondary Address: ${Referral_SecondaryAddress}`}, ${isEmpty(careType) ? "" : `Care type chosen during auth creation:" ${careType},`} ${nurseToOpen && "Nurse to open chosen by requestor"}`;
};

/**
 *
 * @param errorResponse any errorResponse
 * @param setError UseFormSetError<any> setError
 *
 * @returns server side error message
 */
export const serverSideErrorMsg = (errorResponse: any) => {
  return Object.keys(errorResponse).forEach((field) => {
    const messages = errorResponse[field];
    if (messages && isArray(messages)) {
      toastType.error(messages.join(". "));
    }
  });
};

export const isBase64String = (str: string) => {
  const base64Pattern = /^[A-Za-z0-9+/=]+$/;

  if (str.length % 4 === 0 && base64Pattern.test(str)) {
    try {
      atob(str);
      return true;
    } catch (e) {
      return false;
    }
  }
  return false;
};

export const convertTimeToMinutes = (time: any) => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

/**
 *
 * @param pathname
 * @returns
 */
export const checkIsTextPresent = (pathname: string) => {
  if (pathname.includes(Constants.RESET_PASSWORD)) {
    return Numbers.ONE;
  }
  if (pathname.includes(Constants.USER_REGISTRATION)) {
    return Numbers.TWO;
  }
  return Numbers.ZERO;
};

/**
 * Used for logged out user
 * @param redirectUri
 * @param instance
 */
export const Logout = async (redirectUri: string, instance: any) => {
  try {
    await instance.logoutRedirect({
      postLogoutRedirectUri: redirectUri,
    });
  } catch (error) {
    console.error(error);
  }
  sessionStorage.clear();
};
/**
 * to check is dob is 21 or turning 21 soon
 * @param dob
 * @param startDate
 * @param endDate
 * @returns
 */
export const is21OrTurning21Soon = (
  dob: string,
  startDate: string,
  endDate: string
) => {
  const twentyFirstBirthday = dayjs(dob).add(21, "year");
  const end = dayjs(endDate);
  const start = dayjs(startDate);

  return ((twentyFirstBirthday.isSame(start) || twentyFirstBirthday.isAfter(start)) &&
    (twentyFirstBirthday.isSame(end) || twentyFirstBirthday.isBefore(end))) ||
    (twentyFirstBirthday.isSame(start) || twentyFirstBirthday.isBefore(start))
};

/**
 * to check is dob above 21 year
 * @param dateOfBirth
 * @returns
 */
export const isAbove21 = (dateOfBirth: string) => {
  let dob = dayjs(dateOfBirth, "MM/DD/YYYY", true);
  if (!dob.isValid()) {
    dob = dayjs(dateOfBirth, DateFormats.DD_MM_YYYY, true);
  }
  const today = dayjs();
  return today.diff(dob, "year", true) >= 21;
};

/**
 * Checks the eligibility of a member based on the provided CheckEligibility data.
 * The eligibility is determined by two parameters:
 * 1. verificationServiceResponded.
 * 2. memberFound.
 * @param CheckEligibilityData
 * @returns
 */
export const isMemberEligible = (CheckEligibilityData: CheckEligibility) => {
  const result =
    getValue(
      CheckEligibilityData,
      "additionalNotes.verificationServiceResponded",
      false
    ) === true &&
    getValue(
      CheckEligibilityData,
      "supplementalInformation.info[0].memberFound",
      ""
    ).trim() === TextConstants.TRUE
      ? true
      : false;

  return result;
};

/**
 *
 * @param payload
 * @returns
 */
export const getAuthType = (payload: SlotsPayload) => {
  if (payload && payload.type === Numbers.ONE) {
    return `&reimbursementTypeId=${payload.reimbursementTypeId}`;
  }
  return `&referralId=${payload.referralId}`;
};

/**
 *
 * @returns performance period
 */
export const formatPerformancePeriod = (period: string) => {
  if (!period) return "";
  
  const [start, end] = period.split(" - ");
  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split("-");
    return `${new Date(Number(year), Number(month) - 1, Number(day))
      .toLocaleString("default", { month: "short" })
      .toUpperCase()} ${year}`;
  };

  return `${formatDate(start)} - ${formatDate(end)}`;
};

/**
 * 
 * @param length 
 * @returns height of the dropdown
 */
export const getHeight = (length: number) => {
  switch (length) {
    case Numbers.ONE:
      return 60;
    case Numbers.TWO:
      return 90;
    case Numbers.THREE:
      return 150;
    default:
      return 200;
  }
};

/**
 * 
 * @param arr 
 * @param targetValue 
 * @param paramTocheck 
 * @returns true if id available in an array
 */
export const isIdAvailable =(arr:any, targetValue: any, paramTocheck: string) => {
  const valueSet = new Set(arr.map((item: any) => item[paramTocheck]));
  return valueSet.has(targetValue);
}
/**
 * 
 * @param mbi 
 * @returns 
 */
export const getValidMBI = (mbi: any) => {
  return regexMBI.test(mbi) ? mbi : null;
};

/**
 * create pdf of any ui content
 * @param gridRef 
 * @param name 
 */
export const createPdf = async (gridRef: any, name: string) => {
  if (gridRef.current) {
    const canvas = await html2canvas(gridRef.current, {
      scale: 4,
      useCORS: true,
      logging: false,
    });
    const imgData = canvas.toDataURL("image/png");
    const doc = new jsPDF("p", "mm", "a4");
    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const borderMargin = 5;
    const contentWidth = imgWidth - 2 * borderMargin;
    const contentHeight = imgHeight - 2 * borderMargin;
    doc.addImage(
      imgData,
      "PNG",
      borderMargin,
      borderMargin,
      contentWidth,
      contentHeight
    );

    const logoWidth = 15;
    const logoHeight = 5;
    const logoX = imgWidth - borderMargin - logoWidth - 3;
    const logoY = borderMargin + 3;

    doc.addImage(videoPlaceholderImage, "PNG", logoX, logoY, logoWidth, logoHeight);
    doc.setLineWidth(0.5);
    doc.setDrawColor(0, 0, 0);
    doc.rect(borderMargin, borderMargin, contentWidth, contentHeight);

    const timestamp = `Scores displayed reflect data pulled on: ${dayjs().format("MM/DD/YYYY")}`;

    doc.setFontSize(8);
    const textWidth = doc.getTextWidth(timestamp);
    const textX = (imgWidth - textWidth) / 2;
    const textY = 295;

    doc.text(timestamp, textX, textY);
    doc.save(name);
  }
};

export const formatPercentageValue = (value: number | string) => {
  if (typeof value === "number") {
    return `${value.toFixed(2)}%`;
  }
  
  if (typeof value === "string" && value.includes("%")) {
    return parseFloat(value).toFixed(2) + "%";
  }
  
  return value;
};

/**
 * Use this function to download Excel file
 * @param data 
 * @param name 
 */
export const downloadExcelFile = (data: Blob, name: string) => {
  const blob = URL.createObjectURL(data);
  saveAs(blob, name, { autoBom: false });
  URL.revokeObjectURL(blob);
};

/**
 * 
 * @param role 
 * @returns 
 */
export const checkRoleData = (role: MasterRole[]) => {
  if (role !== undefined && role.length > 1) {
    return false;
  }
  return true;
};

/**
 * to check has access or not for logged in user
 * @param roleId 
 * @param featureAccess 
 * @returns 
 */
export const hasAccess = (roleId: string, featureAccess: FeatureAccess[] | null, featureId: number) => {
  const feature = featureAccess !== null && featureAccess.find((item: FeatureAccess) => item.roleId === roleId && featureId === item.featureId)
  return feature && feature.frontEndIsActive
}

/**
 * 
 * @param str
 */
export const removeAccents = (str: string): string =>
  str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

/**
 * Remove special characters from a string
 * @param str 
 * @returns sentence without special chars
 */
  export const removeSpecialChars = (str: string) => {
    return str.replace(removeSpecialChar, '');
}

/**
 * Checks if a value is empty and returns a different value if it is.
 * @param str1 
 * @param str2 
 * @returns   
 */
export const checkIsEmpty = (value1: any, value2: any) => {
  if (isEmpty(value1) || (value1 && value1.length < 1)) {
    return value2;
  }
  return value1
}

/**
 * 
 * @param input
 */
export const formatPhoneNumber = (input: string): string => {
  const digits = typeof input === "string" ? input.replace(/\D/g, "") : "";
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const validateDocumentUpload = (files: any, setError: any, clearErrors: any) => {
    if (files.length === 0) {
      setError("documentUpload", {
        type: "manual",
        message: "Please upload at least one document.",
      });
      return false;
    } else {
      clearErrors("documentUpload");
      return true;
    }
};
  
/**
 * Returns the formatted address for a given item by matching its serviceProviderId
 * with the corresponding provider in the masterServiceProvider list.
 *
 * @param item - The object containing a serviceProviderId to match.
 * @param masterServiceProvider - An array of ServiceProvider objects to search for a matching provider.
 * @returns The formatted address string if a matching provider is found; otherwise, an empty string.
 */
export const getFormattedAddress = (item: any, masterServiceProvider: ServiceProvider[]) => {
    let formattedAddress = "";
    if (masterServiceProvider && item.serviceProviderId) {
      const matchedProvider = masterServiceProvider.find(
      (provider) => provider.serviceProviderId === item.serviceProviderId
      );
      if (matchedProvider && matchedProvider.providerAddress) {
      formattedAddress = matchedProvider.providerAddress;
      }
  }
  return formattedAddress;
}

let languageLookup = new Map<number, string>();

export const initializeLanguageLookup = (languages: Language[] = []) => {
  languageLookup = new Map(
    languages.map(({ languageId, languageName }) => [
      languageId,
      languageName,
    ])
  );
};

export const getLanguageLabel = (
  value: string | number | null | undefined
): string => {
  if (value == null || value === "") return "";

  const id = Number(value);
  const label = languageLookup.get(id);

  return Number.isNaN(id) ? String(value) : label ?? "";
};

export const getLanguageValue = (
  value: string | number | null | undefined
): number | "" => {
  if (value == null || value === "") return "";

  if (typeof value === "number") return value;

  const id = Number(value);

  if (!Number.isNaN(id)) return id;

  for (const [key, label] of languageLookup.entries()) {
    if (label.toLowerCase() === value.trim().toLowerCase()) {
      return key;
    }
  }
  return "";
};

  /**
  * Splits a full name into first and last names.
  * @param fullName - The full name to split.
  * @returns An object containing the first name and last name.
  */
  export const splitMemberName = (fullName: string) => {
    const parts = fullName.trim().split(/\s+/);
    return {
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' ') || '',
    };
  };

/**
 * Generates a payload object for tracking user page views.
 *
 * @param pageName - The name of the page being viewed.
 * @param fullURL - The full URL of the page.
 * @param baseURL - The base URL of the application.
 * @param documentName - The name of the document being viewed, or null if not applicable.
 * @returns An object containing page view information for analytics or tracking purposes.
 */
export const getUaserPageViewPayload = (
  pageName: string,
  fullURL: string,
  baseURL: string,
  documentName: string | null
) => {
  return {
    pageName: pageName,
    fullURL: fullURL,
    baseURL: baseURL,
    documentName: documentName,
  };
};

/**
 * 
 * @returns The full URL of the current page.
 * This function retrieves the current page's URL from the browser's window object.
 */
export const getCurrnetPageFullPath = () => {
  return window.location.href;
}

type NullableString = string | null | undefined;

/**formatted phone number*/
export const getPhoneNumber = (phone: NullableString) => {
    const digits = phone && phone.length > 0 ? phone.replace(/\D/g, "") : "";
    return !isEmpty(digits) && digits.length === 10
      ? "(" +
          digits.substring(0, 3) +
          ") " +
          digits.substring(3, 6) +
          "-" +
          digits.substring(6, 10)
      : "";
};

/**
* Converts bytes to kilobytes and formats the result.
* @param bytes - The number of bytes to convert to kilobytes. 
* @param decimalPlaces - The number of decimal places to round the result to (default is 2).
* @returns A string representing the size in kilobytes, rounded to the specified number of decimal places.
*/
export const bytesToKB = (bytes: number, decimalPlaces: number = 2): string => {
  if (bytes === 0) return "0 KB";
  const kb = bytes / 1024;
  return `${kb.toFixed(decimalPlaces)} KB`;
};

/**
 * Checks if all values in the given object are empty.
 *
 * A value is considered empty if it is `null`, `undefined`, or an empty string (`""`).
 *
 * @param obj - The object whose values will be checked.
 * @returns `true` if every value in the object is empty; otherwise, `false`.
 */
export const isEmptyValue = (obj: { [key: string]: any }): boolean => {
  return Object.values(obj).every(
    (value) => value === null || value === "" || value === undefined
  );
};

export const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

/**
 * Transforms an array of items into a list of dropdown options by extracting specified keys.
 *
 * @param list - The array of items to be transformed.
 * @param keyNames - The keys to extract from each item for the dropdown option.
 * @returns An array of dropdown options extracted from the input list, or an empty array if the input is not an array.
 */
export const getList = (list: any[], keyNames: DropdownOptionKeys) => {
  if (isArray(list)) {
    return list.map((v: any) => extractDropdownOption(v, keyNames));
  }
  return [];
};

/**
 * Extracts specific properties from an item object based on the provided key mapping.
 *
 * @param item - The source object from which to extract properties.
 * @param keyNames - An object mapping output keys to the corresponding keys in the item.
 * @returns An object containing the extracted properties, where each key is from `keyNames` and its value is from `item`.
 */
const extractDropdownOption = (item: any, keyNames: DropdownOptionKeys) => {
  const result: any = {};
  for (const [key, value] of Object.entries(keyNames)) {
    result[key] = item[value];
  }
  return result;
};

/**
 * Determines if the provided eligibility check response meets the required criteria for eligibility.
 *
 * The function checks the following conditions:
 * - `healthPlanUid`, `benefitPlanUid`, and `scrubbedSubscriberID` in `additionalNotes` must not be empty.
 * - The `supplementalInformation.info` array must contain at least one item.
 * - The `memberFound` property of the first item in `supplementalInformation.info` must be equal to `TextConstants.TRUE` (after trimming).
 *
 * @param state - The eligibility check response object to evaluate.
 * @returns `true` if all eligibility criteria are met, otherwise `false`.
 */
export const isEligible = (state: CheckEligibility) => {
  const checkEligibilityState = state;

  if (
    isEmpty(checkEligibilityState?.additionalNotes?.healthPlanUid) ||
    isEmpty(checkEligibilityState?.additionalNotes?.benefitPlanUid) ||
    isEmpty(checkEligibilityState?.additionalNotes?.scrubbedSubscriberID) ||
    size(
      getValue(checkEligibilityState, "supplementalInformation.info", [])
    ) === 0 ||
    getValue(
      checkEligibilityState,
      "supplementalInformation.info[0].memberFound",
      ""
    ).trim() !== TextConstants.TRUE
  ) {
    return false;
  }
  return true;
}

/**
 * Validates whether a given string is a valid phone number.
 *
 * Accepts phone numbers in the following formats:
 * - (123) 456-7890
 * - 1234567890
 *
 * @param phone - The phone number string to validate.
 * @returns `true` if the phone number matches the accepted formats, otherwise `false`.
 */
export const validatePhoneNumber = (phone: string): boolean  => {
  const regex = /^(\(\d{3}\)\s?\d{3}-\d{4}|\d{10})$/;
  return regex.test(phone);
}

/**
 * Formats a mailing address object into a single string, separating each non-empty part by a comma.
 *
 * Filters out any address fields that are `null`, `undefined`, or empty strings.
 * Returns an empty string if the address is not provided.
 *
 * @param address - The mailing address object to format, which may be `null` or `undefined`.
 * @returns A formatted address string, or an empty string if the address is not provided.
 */
export const formatMailingAddress = (address?: InfoAddress | null): string => {
  if (!address) return '';

  const parts = [
    address.addressLine,
    address.addressLine2,
    address.city,
    address.state,
    address.zipCode,
    address.countryCode,
    address.county,
  ];

  const filteredParts = parts.filter(
    (part) => part !== null && part !== undefined && part !== ''
  );

  return filteredParts.join(', ');
}

/**
 * Formats a residential address object into a single string, separating each non-empty part by a comma.
 *
 * Filters out any address fields that are `null`, `undefined`, empty strings, or `false`.
 * Returns an empty string if the address is not provided.
 *
 * @param address - The address object to format, which may be `null` or `undefined`.
 * @returns A formatted address string, or an empty string if the address is not provided.
 */
export const formatResidentialAddress = (address: InfoAddress | null | undefined): string => {
  if (!address) {
    return '';
  }
  const parts = [
    address.addressLine,
    address.addressLine2,
    address.city,
    address.state,
    address.zipCode,
    address.countryCode,
    address.county,
  ];

  const filteredParts = parts.filter(
    (part) => part !== null && part !== undefined && part !== ''
  );

  return filteredParts.join(', ');
}

  /**
   * Extracts only the numeric digits from a given string.
   * @param input - The input string to extract numbers from.
   * @returns A string containing only the digits from the input.
   */
  export const extractNumbers = (input: string) => {
    return input.replace(/\D/g, "");
  };
  
  /**
   * Formats a phone number string into the format "(XXX) XXX-XXXX" if it contains exactly 10 digits.
   * If the input does not contain 10 digits, returns the current phone number value from the provided `getValues` function.
   *
   * @param value - The input string containing the phone number to format.
   * @param getValues - A function that returns an object with a `phoneNumber` property.
   * @returns The formatted phone number string or the current phone number value.
   */
  export const getFormattedPhoneNumber = (value: string, getValues: any) => {
    const digits = extractNumbers(value);
    if (!isEmpty(digits) && digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return typeof getValues === 'function'&& getValues().phoneNumber.length > 9 ? getValues().phoneNumber : '';
  };


/**
 * Determines whether two `DropdownOption` objects or values are equal.
 * 
 * If both `option` and `value` are objects with a `label` property, compares their `label` values.
 * Otherwise, compares the values directly.
 *
 * @param option - The first dropdown option or value to compare.
 * @param value - The second dropdown option or value to compare.
 * @returns `true` if the options or values are considered equal, otherwise `false`.
 */
export const isOptionEqualToValue = (option: DropdownOption, value: DropdownOption) => {
  return typeof option === "object" &&
    option !== null &&
    "label" in option &&
    typeof value === "object" &&
    value !== null &&
    "label" in value
    ? option.label === value.label
    : option === value;
};
/**
 *
 * @param payload api payload
 * @returns payload in query string format
 */
export const GetQueryString = (payload: any) => {
  const QueryString = Object.keys(payload)
    .map((key) =>
      payload[key] !== null && payload[key] !== undefined ? key + "=" + payload[key] : ""
    )
    .filter((str) => str !== "") // Filter out empty strings
    .join("&");

  return QueryString;
};

/**
 *
 *  @returns Array of objects of type State
 */

export const stateListData = (): NppesStateList[] => {
  return Object.entries(stateNameAbbreviations).map(([stateName, stateId]) => ({
    stateId,
    stateName,
  }));
};
/**
 *
 * @param phoneNumber string formatted/not formatted
 * @returns formatted phone number
 */
export const getFormattedPhoneNo = (phoneNumber: NullableString) => {
  if (isNill(phoneNumber) || isUndefined(phoneNumber)) {
    return phoneNumber;
  } else {
    return FormattedNumber(phoneNumber.replace(/\D/g, ""), phoneNumber.length);
  }
};

type KeyValueObject = { [key: string]: any };

/**
 * Retrieves the values of specified form fields using a provided getter function.
 *
 * @param fields - An array of field names whose values are to be retrieved.
 * @param getValues - A function that takes a field name and returns its value.
 * @returns An object mapping each field name to its corresponding value.
 */
export const getFormValues = (
  fields: string[],
  getValues: (field: string) => any
): KeyValueObject => {
  return fields.reduce((acc, field) => {
    acc[field] = getValues(field);
    return acc;
  }, {} as KeyValueObject);
};
/**
 * Moves an item in an array from one position to another.
 * @param array T[] array
 * @param from number from
 * @param to number to
 * @returns array
 */
export function arrayMove<T>(array: T[], from: number, to: number) {
  array = array.slice();
  array.splice(to < 0 ? array.length + to : to, 0, array.splice(from, 1)[0]);
  return array;
}
/**
 *
 * @param value string value
 *
 * @returns extracted doc type
 */
export const extractDocType = (value: NullableString) => {
  if (!value || !value.includes("/")) {
    return DocumentType.INVALID;
  }
  const splitString: any = value.split("/")[1];
  const format: DocumentType = splitString;
  return format;
};

export const getApprovedAuthValueByLabel = (input: any) => {
    if (typeof input === "string") {
      const approvedAuth = ApprovedAuthType.find(
        (item) => item.label.toUpperCase() === input.toUpperCase()
      );
      return approvedAuth ? approvedAuth.value : "";
    }
    return "";
};

/**
 * Calculates the minimum and maximum selectable time range for SOC (Start of Care) based on a reference date,
 * the currently selected date, and an optional re-authorization flag.
 *
 * @param dateFrom - The reference date from which the range is calculated for Prior auth it is Current date and reauth it is Existing Prior auths SOC date.
 * @param selectedDate - The currently selected date.
 * @param isReAuth - Optional flag indicating if the calculation is for re-authorization. If true, the maximum date is set to 14 days after `dateFrom`; otherwise, 30 days.
 * @param socDate - Optional flag indicating soc date to handle BCBS MI healthPlan.
 * @param healthPlan - Optional flag indicating health plan name.
 * @returns An object containing:
 *   - `minDateTime`: The minimum selectable time in milliseconds since epoch.
 *   - `maxDateTime`: The maximum selectable time in milliseconds since epoch.
 *   - `selectedTime`: The selected time in milliseconds since epoch.
 */
export const getSelectedTimeForSOC = (dateFrom: any, selectedDate: any, isReAuth: boolean, healthPlan: string) => {
  const minDate = healthPlan === BCBSMI ? dayjs(BCBSMIMINSOCDATE) : addDaysToDate(dateFrom, -5, "day")
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0);
  const maxDate = addDaysToDate(dateFrom, isReAuth ? 14 : 30, "day")
    .hour(23)
    .minute(59)
    .second(59)
    .millisecond(999);
  const minDateTime = minDate.valueOf();
  const maxDateTime = maxDate.valueOf();
  const selectedTime = new Date(selectedDate).getTime();
  return {
    minDateTime: minDateTime,
    maxDateTime: maxDateTime,
    selectedTime: selectedTime,
  };
};

type Input = {
  healthPlanUid?: string;
  UserDefined6?: string;
  UserDefined7?: string;
};

type Output = {
  subscriberSuffix: string | null;
  groupPlanId: string | null;
  relationshipTypeId: number | null;
};

export const mapUserDefinedFields = (input: Input): Output => {
  const { healthPlanUid, UserDefined6, UserDefined7 } = input;

  const result: Output = {
    subscriberSuffix: null,
    groupPlanId: null,
    relationshipTypeId: null,
  };
  if (healthPlanUid === PayerIdInfo.BCBS_TN_HEALTHPLAN_UID) {
    result.subscriberSuffix = UserDefined6 ?? null;
  }
  if (healthPlanUid === PayerIdInfo.BCBS_MICHIGAN_HEALTHPLAN_UID) {
    result.subscriberSuffix = UserDefined6 ?? null;
    result.groupPlanId = UserDefined7 ?? null;
    result.relationshipTypeId = !isEmpty(UserDefined6) ? Number(UserDefined6) : null;
  }
  return result;
};

/**
 * Returns the date ranges for the last four completed calendar quarters.
 *
 * The current quarter is excluded because its data is not considered complete.
 *
 * @returns 
 * - label: "Q{quarter} {year}"
 * - value: quarter number (1-4)
 * - An array of four QuarterRange objects:
 *    - quarterBegin: YYYY-MM-DD (first day of quarter)
 *    - quarterEnd: YYYY-MM-DD (last day of quarter)
 */
export const getPerformancePeriodsWithRange = (): PerformancePeriods[] => {
  const now = new Date();
  const currentMonth = now.getMonth() + Numbers.ONE;
  const currentYear = now.getFullYear();
  const currentQuarter = Math.ceil(currentMonth / Numbers.THREE);

  const pad = (n: number) => String(n).padStart(2, "0");

  const quarterMonths = [
    { start: 1, end: 3 },
    { start: 4, end: 6 },
    { start: 7, end: 9 },
    { start: 10, end: 12 },
  ];

  const ranges: PerformancePeriods[] = [];

  let q = currentQuarter - Numbers.ONE;
  let year = currentYear;
  if (q === Numbers.ZERO) {
    q = Numbers.Four;
    year = currentYear - Numbers.ONE;
  }

  Array.from({ length: Numbers.Four }).forEach(() => {
    const { start, end } = quarterMonths[q - Numbers.ONE];

    const quarterBegin = `${year}-${pad(start)}-01`;
    const lastDay = new Date(year, end, 0).getDate();
    const quarterEnd = `${year}-${pad(end)}-${pad(lastDay)}`;

    ranges.push({
      label: `Q${q} - ${year}`,
      value: q,
      quarterRange: { quarterBegin, quarterEnd },
    });
    q--;
    if (q === Numbers.ZERO) {
      q = Numbers.Four;
      year--;
    }
  });
  return ranges;
};

/**
 * Reloads the current application page.
 *
 * This function triggers a full page reload using the browser's window.location.reload().
 */
export const reloadApplication = () => {
  window.location.href = window.location.pathname + "?t=" + Date.now().toString();
}


/**
 * Checks if the API response indicates a version conflict.
 *
 * @param response - The API response object.
 * @returns `true` if the response status matches the version conflict status, otherwise `false`.
 */
export const isVersionConflict = (response: any) => {
  return getValue(response, "status") === VERSION_CONFLICT_STATUS;
};

/**
 * Represents the current version of the application as a string loaded from
 * `public/apiBuildVersion.json`.
 */
export const APP_VERSION = fetch("/apiBuildVersion.json", { cache: "no-store" })
  .then((response) => response.json())
  .then((data) => {
    const apiVersion = String(getValue(data, "API_VERSION", ""));
    return apiVersion;
  })
  .catch(() => "");

/**
 * Formats a percentage value without rounding, ensuring that it always has two decimal places.
 *
 * @param value - The value to format.
 * @returns A string representing the formatted percentage
 */
export function formatPercentageNoRoundOff(value: number): string {
  return `${(value * 100).toFixed(2)}%`;
}

export const sortedChartData = (
  chartData: {
    label: string;
    value: number;
    npi: string;
  }[]
) => {
  return [...chartData].sort((a, b) => b.value - a.value);
};

/**
 * Determines if the current user is a BCBS TN user based on the presence and validity of a SAML token in session storage.
 *
 * @returns {boolean} Returns `true` if a non-empty SAML token exists in session storage, indicating a BCBS TN user; otherwise, returns `false`.
 */
export const bcbsTnUser = () => {
  const samlToken = getSessionStorage("samlToken");
  const isBcbs = samlToken !== null  && !isEmpty(samlToken) && samlToken.length > 0;
  return isBcbs;
};

/**
 * Returns the role index of the logged-in user based on `roleAccess1` order.
 *
 * Role index mapping:
 * - 0: Tango Admin
 * - 1: Provider Admin
 * - 2: Provider User
 *
 * @param loggedInUser Current logged-in user payload
 * @returns Matched role index, or `null` if role is not recognized
 */
export const getUserRole = (loggedInUser: MyAccountModel): number | null => {
  const roleType = getValue(
    loggedInUser,
    "providerPortalUser.roleType",
    UserRole.PROVIDER_USER
  );
  const roleIndex = roleAccess1.findIndex(
    (role) => lowerCase(trim(role)) === lowerCase(trim(roleType))
  );
  return roleIndex >= 0 ? roleIndex : null;
};

/**
 * Determines if the current user is a SSO user based on the presence and validity of a SAML token in session storage.
 *
 * @returns {boolean} Returns `true` if a non-empty SAML token exists in session storage, indicating a BCBS TN user; otherwise, returns `false`.
 */
export const checkSSOUser = () => {
  const samlToken = getSessionStorage("samlToken");
  const isBcbs = samlToken !== null  && !isEmpty(samlToken) && samlToken.length > 0;
  return isBcbs;
};

/**
 * Returns environment-specific Tempo configuration values for local development.
 */
export const GetEnvVariables = (): string => {
  let envConfigs = {} as any;

  if (IS_LOCALHOST && process.env.NODE_ENV !== "production") {
    // Loaded lazily and only in non-production builds so localConfig.json is
    // excluded from the Azure release bundle (main.js).
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const localConfig = require("../configs/localConfig.json");
    const selectedEndpointEnv: string = REACT_APP_ENDPOINT_ENV;

    switch (selectedEndpointEnv) {
      case CloudEnvironmentConfig.DEV1:
        envConfigs = localConfig?.[CloudEnvironmentConfig.DEV1] ?? {};
        break;
      case CloudEnvironmentConfig.QA1:
        envConfigs = localConfig?.[CloudEnvironmentConfig.QA1] ?? {};
        break;
      case CloudEnvironmentConfig.DEV2:
        envConfigs = localConfig?.[CloudEnvironmentConfig.DEV2] ?? {};
        break;
      case CloudEnvironmentConfig.QA2:
        envConfigs = localConfig?.[CloudEnvironmentConfig.QA2] ?? {};
        break;
    }
  }
  return envConfigs;
};
/*
 * Encrypts an ID using AES encryption and URL-encodes the result.
 *
 * The encrypted value can be safely passed in URLs, route parameters,
 * or query strings without exposing the original ID.
 *
 * @param id The plain-text ID to encrypt
 * @returns URL-safe AES encrypted string
 */
export const encryptId = (id: string | number): string => {
  return encodeURIComponent(
    CryptoJS.AES.encrypt(
      String(id),
      `${SECRET_KEY}${SALT}`
    ).toString()
  );
};

/**
 * Decrypts an AES-encrypted and URL-encoded ID back to its original value.
 *
 * This function first decodes the URL-safe string and then decrypts it
 * using the same secret key and salt combination used during encryption.
 *
 * @param encryptedId URL-encoded encrypted ID
 * @returns Original decrypted ID as a string
 */
export const decryptId = (encryptedId: string | number): string => {
  const bytes = CryptoJS.AES.decrypt(
    decodeURIComponent(String(encryptedId)),
    `${SECRET_KEY}${SALT}`
  );

  return bytes.toString(CryptoJS.enc.Utf8);
};

/**
 * Determines if eligibility response sends language English or Spanish or Chinese or Arabic.
 *
 * @param checkEligibilityResponse - data when request was received
 * @returns `English or Spanish or Chinese or Arabic or undefined`
 */

export const getLanguage = (checkEligibilityResponse: any) => {
  const inputString = String(checkEligibilityResponse).toLowerCase();

  for (const [languageKey, keywords] of Object.entries(LANGUAGE_KEYWORDS)) {
    if (keywords.some(keyword => inputString.includes(keyword.toLowerCase()))) {
      return PatientLanguages[languageKey as keyof typeof PatientLanguages];
    }
  }

  return undefined;
};
